import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Playground from "../../components/Playground";
import { API_BASE_URL } from "../../utils/api";
import axiosInstance from "../../utils/api";
import {
  fetchAgentById,
  shouldHideDocumentViewerForAgent,
} from "../../utils/agentVariables";
import { normalizeFourayResponseForPlayground } from "../../utils/runAgentResponse";
import { fetchAutomationOutputById } from "../../services/automationOutputService";

/** Playground reads fouray_response.response; normalize flat run-agent records on load. */
const normalizeInvoiceForPlayground = (data) =>
  normalizeFourayResponseForPlayground(data);

const AUTOMATION_OUTPUTS_RESOURCE = "automation_outputs";

const PlaygroundPage = () => {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backLink = searchParams.get("back") || "/skills";
  const hideFromUrl = searchParams.get("hideDocumentViewer") === "1";
  const resourceType =
    searchParams.get("resource") === AUTOMATION_OUTPUTS_RESOURCE
      ? AUTOMATION_OUTPUTS_RESOURCE
      : "invoice_processings";
  const isAutomationOutput = resourceType === AUTOMATION_OUTPUTS_RESOURCE;
  const [hideDocumentViewer, setHideDocumentViewer] = useState(
    hideFromUrl || isAutomationOutput
  );

  useEffect(() => {
    const fetchDocumentData = async () => {
      if (!documentId) {
        setError("No document ID provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        if (isAutomationOutput) {
          const output = await fetchAutomationOutputById(documentId);
          if (!output) {
            throw new Error("Automation output not found");
          }

          const data = normalizeInvoiceForPlayground(output);
          setHideDocumentViewer(true);

          const docData = {
            documentUrl: null,
            documentType: "pdf",
            documentName: data.output_table_name || `Output-${documentId}`,
            apiEndpoint: `${API_BASE_URL}/api/v1/automation_outputs/${documentId}`,
            initialContent: data,
            resourceType: AUTOMATION_OUTPUTS_RESOURCE,
          };

          setDocumentData(docData);
          return;
        }

        const response = await axiosInstance.get(
          `/api/v1/invoice_processings/${documentId}`
        );
        const apiResponse = response.data;
        const invoiceData =
          apiResponse?.data?.data ?? apiResponse?.data ?? apiResponse;
        const data = normalizeInvoiceForPlayground(invoiceData);

        let hideDocViewer = hideFromUrl;
        if (!hideDocViewer && data?.agent_id) {
          try {
            const agent = await fetchAgentById(axiosInstance, data.agent_id);
            hideDocViewer = shouldHideDocumentViewerForAgent(agent);
          } catch (agentErr) {
            console.warn(
              "Could not load agent for document viewer setting:",
              agentErr
            );
          }
        }
        setHideDocumentViewer(hideDocViewer);

        let documentUrl = null;
        if (data.attachment) {
          if (typeof data.attachment === "string") {
            documentUrl = data.attachment;
          } else if (data.attachment.url) {
            documentUrl = data.attachment.url;
          } else if (data.attachment.filename) {
            documentUrl = data.attachment_url || null;
          }
        } else {
          documentUrl = data.attachment_url || null;
        }

        let documentType = "pdf";
        if (documentUrl) {
          const urlLower = documentUrl.toLowerCase();
          if (urlLower.includes(".pdf")) documentType = "pdf";
          else if (urlLower.includes(".jpg") || urlLower.includes(".jpeg"))
            documentType = "image";
          else if (urlLower.includes(".png")) documentType = "image";
        } else if (data.attachment?.content_type) {
          if (data.attachment.content_type.includes("pdf")) documentType = "pdf";
          else if (data.attachment.content_type.includes("image"))
            documentType = "image";
        }

        const documentName =
          data.document_name ||
          data.documentName ||
          data.attachment?.filename ||
          "Document";

        const docData = {
          documentUrl,
          documentType,
          documentName,
          apiEndpoint: `${API_BASE_URL}/api/v1/invoice_processings/${documentId}`,
          initialContent: data,
          resourceType: "invoice_processings",
        };

        setDocumentData(docData);
      } catch (err) {
        console.error("Error fetching playground data:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            (isAutomationOutput
              ? "Failed to load automation output"
              : "Failed to load invoice processing data")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentData();
  }, [documentId, hideFromUrl, isAutomationOutput]);

  if (loading) {
    return (
      <div>
        <div
          className="playground-loading"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading document...</p>
        </div>
      </div>
    );
  }

  if (error && !documentData) {
    return (
      <div>
        <div
          className="playground-error"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p>Error loading document: {error}</p>
        </div>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div>
        <div
          className="playground-error"
          style={{ padding: "40px", textAlign: "center" }}
        >
          <p>No document data available</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Playground
        documentUrl={documentData.documentUrl}
        documentType={documentData.documentType}
        documentName={documentData.documentName}
        apiEndpoint={documentData.apiEndpoint}
        initialContent={documentData.initialContent}
        backLink={backLink}
        backLinkText="Back to Invoice Workbench"
        hideDocumentViewer={hideDocumentViewer}
        resourceType={documentData.resourceType}
      />
    </div>
  );
};

export default PlaygroundPage;
